import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
    AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import {
    ArrowLeft01Icon, CheckmarkCircle02Icon,
    Upload01Icon, PencilEdit01Icon, ArrowRight01Icon,
    Delete02Icon, CloudUploadIcon, File01Icon, Share08Icon,
} from '@hugeicons/core-free-icons';
import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SnackbarService } from '../../../../core/services/snackbar.service';
import { DocumentService } from '../../../../core/services/document.service';
import { CaseService } from '../../../../core/services/case.service';
import { DocumentCaseData, DocumentId, DocumentSignedStatus } from '../../../../core/services/document.models';
import { SelectOption } from '../../../../shared/components/select/select.component';

import { DOC_TYPE_MAP } from '../../../../core/services/document-api.constants';
import { DocSignState, DocDefinition, blankSigner } from './doc-models';
import { DocAcknowledgmentComponent }       from './doc-acknowledgment/doc-acknowledgment.component';
import { DocAssignmentTrustFundsComponent } from './doc-assignment-trust-funds/doc-assignment-trust-funds.component';
import { DocHipaaAuthorizationComponent }   from './doc-hipaa-authorization/doc-hipaa-authorization.component';
import { DocReleasOfLienComponent }         from './doc-release-of-lien/doc-release-of-lien.component';

export type { DocDefinition };

type ModalMode   = 'select' | 'upload' | 'esign';
type EsignView   = 'list'   | 'sign';

@Component({
    selector: 'app-documents-preview-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ModalComponent,
        HugeiconsIconComponent,
        DocAcknowledgmentComponent,
        DocAssignmentTrustFundsComponent,
        DocHipaaAuthorizationComponent,
        DocReleasOfLienComponent,
    ],
    templateUrl: './documents-preview-modal.component.html',
    styleUrl: './documents-preview-modal.component.scss',
})
export class DocumentsPreviewModalComponent implements OnChanges, AfterViewChecked {

    // ── Inputs / Outputs ───────────────────────────────────────────────────
    @Input() isOpen  = false;
    @Input() caseId: number | string | null = null;
    /** Which document tab to open first when opened in e-sign mode directly. */
    @Input() startDocIndex = 0;
    /**
     * When set to 'esign', the modal skips the mode-selection screen and
     * opens directly in the e-sign sign view at `startDocIndex`.
     * Defaults to 'select' (normal mode-selection screen).
     */
    @Input() startMode: ModalMode = 'select';
    /** When non-empty, only these doc indices (0/1/2) are shown in upload & e-sign listings. */
    @Input() rejectedDocIndices: number[] = [];
    /** Offer documents from the API — used to check which docs are already signed. */
    @Input() offerDocuments: any[] = [];

    /** When set, only doc IDs in this list are visible (e.g. ['assignment-trust-funds','hipaa-authorization']). */
    @Input() allowedDocIds: string[] | null = null;

    /** When set, the modal renders inline (no backdrop) — for the public client page. */
    @Input() inline = false;

    /** When set, /api/upload-signed will receive token+pin in the FormData (client page). */
    @Input() clientAuth: { token: string; pin: string } | null = null;

    /** When set, use this DocumentCaseData for all docs instead of calling /api/case-data/... */
    @Input() preloadedDocCaseData: DocumentCaseData | null = null;

    @Output() close = new EventEmitter<void>();

    // ── Child component refs ───────────────────────────────────────────────
    @ViewChild(DocAcknowledgmentComponent)       acknowledgmentComp!: DocAcknowledgmentComponent;
    @ViewChild(DocAssignmentTrustFundsComponent) atfComp!:            DocAssignmentTrustFundsComponent;
    @ViewChild(DocHipaaAuthorizationComponent)   hipaaComp!:          DocHipaaAuthorizationComponent;
    @ViewChild(DocReleasOfLienComponent)        releaseComp!:        DocReleasOfLienComponent;
    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

    // ── Icons ──────────────────────────────────────────────────────────────
    prevIcon        = ArrowLeft01Icon;
    nextIcon        = ArrowRight01Icon;
    checkIcon       = CheckmarkCircle02Icon;
    uploadIcon      = Upload01Icon;
    signIcon        = PencilEdit01Icon;
    deleteIcon      = Delete02Icon;
    cloudUploadIcon = CloudUploadIcon;
    fileIcon        = File01Icon;
    shareIcon       = Share08Icon;

    // ── Mode state ─────────────────────────────────────────────────────────
    mode: ModalMode    = 'select';
    esignView: EsignView = 'list';
    /** Internal visibility flag — used to briefly close/reopen the modal on mode selection. */
    visibleOpen = false;

    get modalTitle(): string {
        if (this.mode === 'select') return 'Document Upload & Sign';
        if (this.mode === 'upload') return 'Upload Documents';
        if (this.esignView === 'list') return 'E-Sign Documents';
        return `Sign: ${this.docs[this.activeDocIndex]?.title ?? 'Document'}`;
    }

    // ── Document definitions ───────────────────────────────────────────────
    activeDocIndex = 0;
    readonly docs: DocDefinition[] = [
        { id: 'acknowledgment',         title: 'Acknowledgment of Assignment',                               subtitle: '(Patient Name / Provider)',     signerLabel: 'By Injury Counsel' },
        { id: 'assignment-trust-funds', title: 'Assignment of Trust Funds, Obligations & Responsibilities',  subtitle: '(Patient Name / Provider)',     signerLabel: 'By Patient & Attorney' },
        { id: 'hipaa-authorization',    title: 'HIPAA Authorization',                                        subtitle: '',                              signerLabel: 'By Patient' },
        { id: 'release-of-lien',        title: 'Release of Lien & Authority to Distribute',                    subtitle: '(Patient Name / Provider)',     signerLabel: 'By Provider' },
    ];

    // ── Upload mode state ──────────────────────────────────────────────────
    /** Document type options remaining to be uploaded */
    uploadTypeOptions: SelectOption[] = [];
    selectedUploadType = '';
    /** Files queued for upload: { id (doc type id/value), file, fileName, label } */
    selectedUploadDocs: { id: string; file: File; fileName: string; label: string }[] = [];
    uploading = false;

    // ── E-Sign mode state ──────────────────────────────────────────────────
    states: DocSignState[] = [];
    /** Blobs generated by signing each doc — used for bulk submit */
    signedBlobs: (Blob | null)[] = [null, null, null, null];
    /** Per-doc signed dates */
    signedDates: string[] = ['', '', '', ''];

    /** Progress percentage for PDF generation / upload (null = not active) */
    progressPercent: number | null = null;
    progressLabel = '';

    /** Patient/provider/offer data — one entry per document */
    caseDataPerDoc: (DocumentCaseData | null)[] = [null, null, null, null];
    isLoadingData = false;
    serverSignedStatus: DocumentSignedStatus | null = null;

    private canvasesInitialized = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private snackbar: SnackbarService,
        private documentService: DocumentService,
        private caseService: CaseService,
    ) { this.initStates(); }

    // ── Lifecycle ──────────────────────────────────────────────────────────
    private initStates(): void {
        this.states    = this.docs.map(() => ({ ...blankSigner(), attorney: blankSigner() }));
        this.signedBlobs = [null, null, null, null];
        this.signedDates = ['', '', '', ''];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen']?.currentValue) {
            this.initStates();
            this.activeDocIndex      = Math.max(0, Math.min(this.startDocIndex, this.docs.length - 1));
            this.canvasesInitialized = false;
            this.serverSignedStatus  = null;
            this.clientSubmittedIndices = new Set<number>();
            this.caseDataPerDoc      = [null, null, null, null];
            this.selectedUploadDocs  = [];
            this.selectedUploadType  = '';
            this.progressPercent     = null;
            this.progressLabel       = '';
            this.submittingCurrentDoc = false;
            this.submittingEsign     = false;
            this.visibleOpen         = true;

            // If caller requests e-sign mode directly, skip the selection screen
            if (this.startMode === 'esign') {
                this.mode      = 'esign';
                // Inline mode (client page) → skip the list, go straight to sign view of first allowed doc
                if (this.inline) {
                    this.esignView = 'sign';
                    const visible = this.visibleDocIndices;
                    if (visible.length > 0) this.activeDocIndex = visible[0];
                } else {
                    this.esignView = 'sign';
                }
                // Load data for the active document immediately (direct entry)
                this.loadDocCaseData(this.activeDocIndex);
            } else {
                this.mode      = 'select';
                this.esignView = 'list';
                // Data is loaded per-document when the user clicks Sign
            }
        }
        if (changes['isOpen'] && !changes['isOpen'].currentValue) {
            this.visibleOpen = false;
            // Modal closed — reset upload file input if present
            if (this.fileInputRef?.nativeElement) this.fileInputRef.nativeElement.value = '';
        }
    }

    // ── Data loading ───────────────────────────────────────────────────────
    // Called only when the user clicks Sign on a specific document (lazy, per-doc).
    private loadDocCaseData(docIndex: number): void {
        // Client page provides preloaded data for all docs — skip the API call
        if (this.preloadedDocCaseData) {
            if (this.caseDataPerDoc[docIndex] === null) {
                this.caseDataPerDoc[docIndex] = this.preloadedDocCaseData;
                this.cdr.detectChanges();
            }
            return;
        }
        if (!this.caseId) return;
        // Skip if this doc's data is already loaded
        if (this.caseDataPerDoc[docIndex] !== null) return;

        this.isLoadingData = true;

        this.documentService.getCaseDataByType(this.caseId, docIndex as 0 | 1 | 2 | 3).pipe(
            catchError(() => of(null))
        ).subscribe(data => {
            this.caseDataPerDoc[docIndex] = data;
            this.isLoadingData = false;
            this.cdr.detectChanges();
        });
    }

    // ── Mode helpers ───────────────────────────────────────────────────────
    selectMode(m: ModalMode): void {
        // Close the select modal, then reopen with the chosen mode
        this.visibleOpen = false;
        setTimeout(() => {
            this.mode = m;
            if (m === 'esign') {
                this.esignView      = 'list';
                this.activeDocIndex = 0;
                this.canvasesInitialized = false;
            }
            if (m === 'upload') {
                this.updateUploadTypeOptions();
            }
            this.visibleOpen = true;
            this.cdr.detectChanges();
        }, 250);
    }

    goBackToSelect(): void {
        this.visibleOpen = false;
        setTimeout(() => {
            this.mode      = 'select';
            this.esignView = 'list';
            this.visibleOpen = true;
            this.cdr.detectChanges();
        }, 250);
    }

    // ── Visible docs (filtered by rejectedDocIndices when non-empty) ─────
    /** Returns the doc indices that should be shown in upload / e-sign listings.
     *  Index 3 (Release of Lien) is always excluded — it is generated from the admin side. */
    private get visibleDocIndices(): number[] {
        let base: number[];
        if (this.rejectedDocIndices.length > 0) base = this.rejectedDocIndices.filter(i => i !== 3);
        else base = [0, 1, 2];
        if (this.allowedDocIds && this.allowedDocIds.length > 0) {
            base = base.filter(i => this.allowedDocIds!.includes(this.docs[i].id));
        }
        return base;
    }

    // ── Upload mode ────────────────────────────────────────────────────────
    private updateUploadTypeOptions(): void {
        // Build list of doc types that are NOT already server-signed
        const alreadySigned = new Set(
            (this.serverSignedStatus?.documents || [])
                .filter(d => d.signed)
                .map(d => d.document_id)
        );
        // Remove any that the user already queued in this session
        const queued = new Set(this.selectedUploadDocs.map(d => d.id));
        const visible = new Set(this.visibleDocIndices);

        this.uploadTypeOptions = this.docs
            .map((doc, i) => ({ label: doc.title, value: String(i) }))
            .filter(opt => visible.has(Number(opt.value)) && !alreadySigned.has(Number(opt.value) as DocumentId) && !queued.has(opt.value) && !this.isSignedOnServer(Number(opt.value)));
    }

    triggerFileInput(): void {
        if (!this.selectedUploadType) {
            this.snackbar.show('Please select a document type first', 'error');
            return;
        }
        this.fileInputRef?.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file  = input.files?.[0];
        if (!file || !this.selectedUploadType) return;

        const opt = this.uploadTypeOptions.find(o => o.value === this.selectedUploadType);
        this.selectedUploadDocs.push({
            id:       this.selectedUploadType,
            file,
            fileName: file.name,
            label:    opt?.label ?? 'Document',
        });
        // Remove from available options
        this.uploadTypeOptions = this.uploadTypeOptions.filter(o => o.value !== this.selectedUploadType);
        this.selectedUploadType = '';
        input.value = '';
    }

    removeUploadDoc(index: number): void {
        const removed = this.selectedUploadDocs.splice(index, 1)[0];
        const doc = this.docs[Number(removed.id)];
        if (doc) {
            this.uploadTypeOptions = [...this.uploadTypeOptions, { label: doc.title, value: removed.id }];
        }
    }

    get allUploadTypesQueued(): boolean {
        return this.uploadTypeOptions.length === 0 && this.selectedUploadDocs.length > 0;
    }

    submitUploadDocuments(): void {
        if (!this.selectedUploadDocs.length) {
            this.snackbar.show('Please attach at least one document', 'error');
            return;
        }
        if (!this.caseId) {
            this.snackbar.show('Case ID is missing', 'error');
            return;
        }
        this.uploading = true;
        const docsToUpload = this.selectedUploadDocs.map(d => ({
            id: DOC_TYPE_MAP[Number(d.id) as 0 | 1 | 2 | 3],
            file: d.file,
        }));

        this.caseService.uploadSignedDocuments(this.caseId, docsToUpload).subscribe({
            next: () => {
                this.snackbar.show('Documents uploaded successfully', 'success');
                this.uploading           = false;
                this.selectedUploadDocs  = [];
                this.initStates();
                this.mode                = 'select';
                this.esignView           = 'list';
                this.activeDocIndex      = 0;
                this.canvasesInitialized = false;
                this.close.emit();
            },
            error: () => {
                this.snackbar.show('Error uploading documents', 'error');
                this.uploading = false;
            },
        });
    }

    /** Indices of docs the client has just submitted in this session (inline mode). */
    clientSubmittedIndices = new Set<number>();

    // ── E-Sign list helpers ────────────────────────────────────────────────
    get esignDocs(): { index: number; id: string; title: string; signed: boolean; shareable: boolean }[] {
        return this.visibleDocIndices
            .filter(i => !this.isSignedOnServer(i) && !this.clientSubmittedIndices.has(i))
            .map(i => ({
                index:  i,
                id:     this.docs[i].id,
                title:  this.docs[i].title,
                signed: this.isDocSigned(i),
                shareable: this.docs[i].id === 'assignment-trust-funds' || this.docs[i].id === 'hipaa-authorization',
            }));
    }

    // ── Share document state ───────────────────────────────────────────────
    showShareSuccessModal = false;
    sharingDocument = false;
    shareSuccessMessage = '';

    shareWithClient(): void {
        if (!this.caseId) {
            this.snackbar.show('Case ID is missing', 'error');
            return;
        }
        if (this.sharingDocument) return;
        this.sharingDocument = true;
        this.caseService.generateClientLink(this.caseId).subscribe({
            next: (res: any) => {
                this.sharingDocument = false;
                this.shareSuccessMessage = res?.message || res?.data?.message || 'E-Sign document shared successfully';
                this.showShareSuccessModal = true;
            },
            error: (err) => {
                this.sharingDocument = false;
                const msg = err?.error?.message || 'Failed to share document. Please try again.';
                this.snackbar.show(msg, 'error');
            },
        });
    }

    closeShareSuccessModal(): void {
        this.showShareSuccessModal = false;
        this.shareSuccessMessage = '';
    }

    get allEsigned(): boolean {
        return this.visibleDocIndices.every(i => this.isDocSigned(i));
    }

    openSignView(docIndex: number): void {
        this.activeDocIndex      = docIndex;
        this.esignView           = 'sign';
        this.canvasesInitialized = false;
        // Load this document's case data now (single API call, skipped if already loaded)
        this.loadDocCaseData(docIndex);
    }

    goBackToEsignList(): void {
        this.esignView = 'list';
    }

    // ── Signed state helpers ───────────────────────────────────────────────
    isDocSigned(i: number): boolean {
        if (this.isSignedOnServer(i)) return true;
        const s = this.states[i];
        if (i === 1) return !s.isDrawEmpty && !!s.selectedDate;
        return !s.isDrawEmpty && !!s.selectedDate;
    }

    /** Map frontend doc index to API document_type values (case-insensitive match) */
    private readonly docTypeNames: Record<number, string[]> = {
        0: ['acknowledgment of assignment'],
        1: ['assignment of trust funds'],
        2: ['hipaa authorization'],
    };

    isSignedOnServer(i: number): boolean {
        if (this.serverSignedStatus?.documents[i]?.signed === true) return true;
        // Check offerDocuments from parent
        if (!this.offerDocuments?.length) return false;
        const names = this.docTypeNames[i] || [];
        return this.offerDocuments.some((doc: any) =>
            names.some(n => (doc.document_type || '').toLowerCase().includes(n)) &&
            !!doc.file_name_signed &&
            doc.accept_status !== 2 && doc.accept_status !== '2'
        );
    }

    getServerSignedUrl(i: number): string | null {
        return this.serverSignedStatus?.documents[i]?.file_url ?? null;
    }

    // ── Canvas initialisation ──────────────────────────────────────────────
    ngAfterViewChecked(): void {
        if (this.visibleOpen && this.mode === 'esign' && this.esignView === 'sign' && !this.canvasesInitialized) {
            const hasCanvas = !!(this.getPatientCanvas() || this.getAttorneyCanvas());
            if (hasCanvas) {
                this.initAllCanvases();
                this.canvasesInitialized = true;
            }
        }
    }

    private getPatientCanvas(): HTMLCanvasElement | null {
        const comp = this.activeComp();
        if (!comp) return null;
        return (comp as any).sigCanvas?.nativeElement ?? null;
    }

    private getAttorneyCanvas(): HTMLCanvasElement | null {
        if (!(this.atfComp && this.activeDocIndex === 1)) return null;
        return this.atfComp.attyCanvas?.nativeElement ?? null;
    }

    private activeComp(): DocAcknowledgmentComponent | DocAssignmentTrustFundsComponent | DocHipaaAuthorizationComponent | DocReleasOfLienComponent | null {
        switch (this.activeDocIndex) {
            case 0: return this.acknowledgmentComp ?? null;
            case 1: return this.atfComp            ?? null;
            case 2: return this.hipaaComp          ?? null;
            case 3: return this.releaseComp        ?? null;
            default: return null;
        }
    }

    private getDocCardEl(): HTMLElement | null {
        const comp = this.activeComp();
        if (!comp) return null;
        return (comp as any).docCard?.nativeElement ?? null;
    }

    private initCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
        const rect = canvas.getBoundingClientRect();
        canvas.width  = rect.width  > 0 ? rect.width  : (canvas.offsetWidth  || 600);
        canvas.height = rect.height > 0 ? rect.height : (canvas.offsetHeight || 90);
        const ctx = canvas.getContext('2d')!;
        ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.fillStyle = '#1a1a2e';
        return ctx;
    }

    private initAllCanvases(): void {
        const pat = this.getPatientCanvas();
        if (pat)  this.states[this.activeDocIndex].ctx          = this.initCanvas(pat);
        const att = this.getAttorneyCanvas();
        if (att)  this.states[this.activeDocIndex].attorney.ctx = this.initCanvas(att);
    }

    // ── Patient draw event handlers ────────────────────────────────────────
    onMouseDown(e: MouseEvent, i: number): void {
        const s = this.states[i]; const c = this.getPatientCanvas(); if (!c) return;
        s.drawing = true;
        const p = this.posFromMouse(e, c);
        s.lastX = p.x; s.lastY = p.y; s.currentStroke = { points: [p] };
        if (s.ctx) { s.ctx.beginPath(); s.ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2); s.ctx.fill(); }
    }
    onMouseMove(e: MouseEvent, i: number): void {
        const s = this.states[i]; if (!s.drawing || !s.ctx) return; e.preventDefault();
        const c = this.getPatientCanvas(); if (!c) return;
        const p = this.posFromMouse(e, c);
        s.ctx.beginPath(); s.ctx.moveTo(s.lastX, s.lastY); s.ctx.lineTo(p.x, p.y); s.ctx.stroke();
        s.lastX = p.x; s.lastY = p.y; s.currentStroke!.points.push(p); s.isDrawEmpty = false;
    }
    onMouseUp(i: number): void { const s = this.states[i]; if (s.drawing) this.endStroke(s); s.drawing = false; }
    onTouchStart(e: TouchEvent, i: number): void {
        e.preventDefault(); const s = this.states[i]; const c = this.getPatientCanvas(); if (!c) return;
        s.drawing = true;
        const p = this.posFromTouch(e.touches[0], c);
        s.lastX = p.x; s.lastY = p.y; s.currentStroke = { points: [p] };
        if (s.ctx) { s.ctx.beginPath(); s.ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2); s.ctx.fill(); }
    }
    onTouchMove(e: TouchEvent, i: number): void {
        e.preventDefault(); const s = this.states[i]; if (!s.drawing || !s.ctx) return;
        const c = this.getPatientCanvas(); if (!c) return;
        const p = this.posFromTouch(e.touches[0], c);
        s.ctx.beginPath(); s.ctx.moveTo(s.lastX, s.lastY); s.ctx.lineTo(p.x, p.y); s.ctx.stroke();
        s.lastX = p.x; s.lastY = p.y; s.currentStroke!.points.push(p); s.isDrawEmpty = false;
    }
    onTouchEnd(i: number): void { const s = this.states[i]; if (s.drawing) this.endStroke(s); s.drawing = false; }

    // ── Attorney draw event handlers ───────────────────────────────────────
    onAttyMouseDown(e: MouseEvent, i: number): void {
        const a = this.states[i].attorney; const c = this.getAttorneyCanvas(); if (!c) return;
        a.drawing = true;
        const p = this.posFromMouse(e, c);
        a.lastX = p.x; a.lastY = p.y; a.currentStroke = { points: [p] };
        if (a.ctx) { a.ctx.beginPath(); a.ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2); a.ctx.fill(); }
    }
    onAttyMouseMove(e: MouseEvent, i: number): void {
        const a = this.states[i].attorney; if (!a.drawing || !a.ctx) return; e.preventDefault();
        const c = this.getAttorneyCanvas(); if (!c) return;
        const p = this.posFromMouse(e, c);
        a.ctx.beginPath(); a.ctx.moveTo(a.lastX, a.lastY); a.ctx.lineTo(p.x, p.y); a.ctx.stroke();
        a.lastX = p.x; a.lastY = p.y; a.currentStroke!.points.push(p); a.isDrawEmpty = false;
    }
    onAttyMouseUp(i: number): void { const a = this.states[i].attorney; if (a.drawing) this.endStroke(a); a.drawing = false; }
    onAttyTouchStart(e: TouchEvent, i: number): void {
        e.preventDefault(); const a = this.states[i].attorney; const c = this.getAttorneyCanvas(); if (!c) return;
        a.drawing = true;
        const p = this.posFromTouch(e.touches[0], c);
        a.lastX = p.x; a.lastY = p.y; a.currentStroke = { points: [p] };
        if (a.ctx) { a.ctx.beginPath(); a.ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2); a.ctx.fill(); }
    }
    onAttyTouchMove(e: TouchEvent, i: number): void {
        e.preventDefault(); const a = this.states[i].attorney; if (!a.drawing || !a.ctx) return;
        const c = this.getAttorneyCanvas(); if (!c) return;
        const p = this.posFromTouch(e.touches[0], c);
        a.ctx.beginPath(); a.ctx.moveTo(a.lastX, a.lastY); a.ctx.lineTo(p.x, p.y); a.ctx.stroke();
        a.lastX = p.x; a.lastY = p.y; a.currentStroke!.points.push(p); a.isDrawEmpty = false;
    }
    onAttyTouchEnd(i: number): void { const a = this.states[i].attorney; if (a.drawing) this.endStroke(a); a.drawing = false; }

    private endStroke(s: import('./doc-models').SignerState): void {
        if (s.currentStroke?.points.length) s.strokes.push(s.currentStroke);
        s.currentStroke = null;
    }

    clearCanvas(i: number): void {
        const s = this.states[i]; const c = this.getPatientCanvas();
        if (c && s.ctx) s.ctx.clearRect(0, 0, c.width, c.height);
        s.strokes = []; s.currentStroke = null; s.isDrawEmpty = true; s.drawing = false;
    }

    clearAttyCanvas(i: number): void {
        const a = this.states[i].attorney; const c = this.getAttorneyCanvas();
        if (c && a.ctx) a.ctx.clearRect(0, 0, c.width, c.height);
        a.strokes = []; a.currentStroke = null; a.isDrawEmpty = true; a.drawing = false;
    }

    // ── Coord helpers ──────────────────────────────────────────────────────
    private posFromMouse(e: MouseEvent, canvas: HTMLCanvasElement): import('./doc-models').StrokePoint {
        const r = canvas.getBoundingClientRect();
        return { x: (e.clientX - r.left) * (canvas.width / r.width), y: (e.clientY - r.top) * (canvas.height / r.height) };
    }
    private posFromTouch(t: Touch, canvas: HTMLCanvasElement): import('./doc-models').StrokePoint {
        const r = canvas.getBoundingClientRect();
        return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
    }

    // ── Progress helpers ─────────────────────────────────────────────────────
    private setProgress(percent: number, label: string): void {
        this.progressPercent = Math.min(percent, 100);
        this.progressLabel = label;
        this.cdr.detectChanges();
    }

    private resetProgress(): void {
        this.progressPercent = null;
        this.progressLabel = '';
        this.cdr.detectChanges();
    }

    // ── PDF generation (e-sign) ────────────────────────────────────────────
    /**
     * Server-side PDF rendering via Gotenberg (headless Chromium).
     *
     * The frontend no longer rasterizes the DOM. We capture the user's signature
     * from the canvas as PNG, send it to the backend along with the document
     * type / signed date, and the backend templates the document, embeds the
     * signature, and returns a real text-based multi-page PDF.
     *
     * Used by both downloadPdf() (download only) and submitCurrentDoc() (upload only).
     */
    private async generatePdfBlob(docIndex: number): Promise<{ blob: Blob; date: string }> {
        const s = this.states[docIndex];

        this.setProgress(5, 'Preparing document…');

        if (s.currentStroke?.points.length)          { s.strokes.push(s.currentStroke); s.currentStroke = null; }
        if (s.attorney.currentStroke?.points.length) { s.attorney.strokes.push(s.attorney.currentStroke); s.attorney.currentStroke = null; }

        const realCardEl = this.getDocCardEl();
        const canvasSnapshots: string[] = [];
        if (realCardEl) {
            realCardEl.querySelectorAll<HTMLCanvasElement>('canvas').forEach(c => {
                canvasSnapshots.push(c.toDataURL('image/png'));
            });
        }

        this.setProgress(10, 'Capturing signatures…');

        await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        await new Promise(r => setTimeout(r, 50));
        await document.fonts.ready;

        this.setProgress(15, 'Loading fonts…');

        const RENDER_W = 960;
        const wrapper  = document.createElement('div');
        wrapper.style.cssText = [
            'position:absolute', 'top:-99999px', 'left:0',
            'width:' + RENDER_W + 'px', 'opacity:0', 'pointer-events:none', 'overflow:visible',
        ].join(';');
        document.body.appendChild(wrapper);

        try {
            if (!realCardEl) throw new Error('doc-card not found');

            this.setProgress(20, 'Cloning document…');

            const clone = realCardEl.cloneNode(true) as HTMLElement;
            clone.style.cssText += ';width:' + RENDER_W + 'px;min-width:' + RENDER_W + 'px;max-width:' + RENDER_W + 'px;overflow:visible;';
            wrapper.appendChild(clone);

            clone.querySelectorAll<HTMLCanvasElement>('canvas').forEach((canvas, i) => {
                const dataUrl = canvasSnapshots[i];
                if (!dataUrl) return;
                canvas.width  = canvas.offsetWidth  || 600;
                canvas.height = canvas.offsetHeight || 90;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                const img = new Image();
                img.src = dataUrl;
                img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                if (img.complete) ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            });

            await new Promise(r => setTimeout(r, 16));

            this.setProgress(30, 'Preparing layout…');
            this.prepareSigBlocksForPdf(clone, docIndex, s);

            const SCALE = 4;
            const PDF_W = 612;      // US Letter width in points
            const PDF_H = 792;      // US Letter height in points
            const pdfDoc = await PDFDocument.create();

            this.setProgress(40, 'Rendering document…');

            // The ATF factor (By Factor) block is pre-filled, fixed info — restyle
            // it to a compact card so it sits inline with the patient signature
            // instead of forcing its own page.
            if (docIndex === 1) {
                const pageBreakEl = clone.querySelector<HTMLElement>('.atf-page-break');
                if (pageBreakEl) pageBreakEl.style.display = 'none';
                this.compactFactorBlock(clone);
            }

            await this.appendSnapPage(clone, SCALE, RENDER_W, PDF_W, PDF_H, pdfDoc);
            this.setProgress(80, 'Embedding page…');

            this.setProgress(90, 'Saving PDF…');
            const bytes   = await pdfDoc.save({ useObjectStreams: false });
            const pdfBlob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });

            this.setProgress(100, 'Complete!');
            return { blob: pdfBlob, date: s.selectedDate };

        } finally {
            wrapper.remove();
        }
    }

    /**
     * Captures a DOM element as one or more PDF pages.
     *
     * Multi-page logic: instead of slicing the rasterized canvas at fixed pixel
     * intervals (which can cut a paragraph or signature line in half), we
     * compute "safe break points" — the y-offset of every direct child of
     * `.atf-body / .ack-body / .hip-body / .rol-body / .cl-body`. Each PDF page
     * fills as many full elements as possible, then breaks BEFORE the next one.
     * The footer is always pinned to the bottom of every page.
     *
     * forceSinglePage = true scales the body proportionally so it fits one page
     * (used for short signature-only pages such as the ATF "By Factor" page).
     */
    private async appendSnapPage(el: HTMLElement, scale: number, renderW: number, pdfW: number, pdfPageH: number, pdfDoc: PDFDocument, forceSinglePage: boolean = false): Promise<void> {
        const h2cOpts = (h: number) => ({
            scale, useCORS: true, allowTaint: true, logging: false,
            backgroundColor: '#ffffff', width: renderW, height: h,
            scrollX: 0, scrollY: 0, windowWidth: renderW,
            onclone: (_: Document, cloned: HTMLElement) => {
                const s = cloned.ownerDocument.createElement('style');
                s.textContent = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;900&display=swap');`;
                cloned.ownerDocument.head.insertBefore(s, cloned.ownerDocument.head.firstChild);
            },
        });

        const SAFE_AREA_PT = 18; // safe-area gap between body content and footer (and top header) — ~0.25in

        // ── Identify the body container and split sibling children into header / footer groups
        const bodySel = '.atf-body, .ack-body, .hip-body, .rol-body, .cl-body';
        const bodyEl  = el.querySelector<HTMLElement>(bodySel);
        const pageEl  = bodyEl?.parentElement ?? null;

        const headerChildren: HTMLElement[] = [];
        const footerChildren: HTMLElement[] = [];
        if (bodyEl && pageEl) {
            let beforeBody = true;
            Array.from(pageEl.children).forEach(c => {
                if (c === bodyEl) { beforeBody = false; return; }
                if (beforeBody) headerChildren.push(c as HTMLElement);
                else            footerChildren.push(c as HTMLElement);
            });
        }

        const setDisplay = (els: HTMLElement[], val: string): string[] =>
            els.map(e => { const prev = e.style.display; e.style.display = val; return prev; });
        const restore = (els: HTMLElement[], prev: string[]) =>
            els.forEach((e, i) => e.style.display = prev[i]);

        // ── Snap header (everything in pageEl above the body)
        let headerCanvas: HTMLCanvasElement | null = null;
        if (bodyEl && headerChildren.length) {
            const prevBody = bodyEl.style.display; bodyEl.style.display = 'none';
            const prevF    = setDisplay(footerChildren, 'none');
            headerCanvas   = await html2canvas(el, h2cOpts(el.scrollHeight));
            restore(footerChildren, prevF);
            bodyEl.style.display = prevBody;
        }

        // ── Snap footer (everything in pageEl below the body)
        let footerCanvas: HTMLCanvasElement | null = null;
        if (bodyEl && footerChildren.length) {
            const prevBody = bodyEl.style.display; bodyEl.style.display = 'none';
            const prevH    = setDisplay(headerChildren, 'none');
            footerCanvas   = await html2canvas(el, h2cOpts(el.scrollHeight));
            restore(headerChildren, prevH);
            bodyEl.style.display = prevBody;
        }

        // ── Snap body alone, and collect element-boundary break points within
        //    the same hidden state so the rects we measure match the canvas.
        let bodyCanvas: HTMLCanvasElement;
        const breaks: number[] = [0];
        if (bodyEl) {
            const prevH = setDisplay(headerChildren, 'none');
            const prevF = setDisplay(footerChildren, 'none');
            void el.offsetHeight; // force layout
            const bodyRect = bodyEl.getBoundingClientRect();
            Array.from(bodyEl.children).forEach(c => {
                const r = (c as HTMLElement).getBoundingClientRect();
                breaks.push((r.top    - bodyRect.top) * scale);
                breaks.push((r.bottom - bodyRect.top) * scale);
            });
            bodyCanvas = await html2canvas(el, h2cOpts(el.scrollHeight));
            restore(headerChildren, prevH);
            restore(footerChildren, prevF);
        } else {
            // Fallback: capture entire element as the body
            bodyCanvas = await html2canvas(el, h2cOpts(el.scrollHeight));
        }

        // Clamp / dedupe break candidates to within the captured body canvas.
        const safeBreaks = Array.from(new Set(
            breaks.map(b => Math.max(0, Math.min(bodyCanvas.height, Math.round(b))))
        )).sort((a, b) => a - b);
        if (safeBreaks[safeBreaks.length - 1] !== bodyCanvas.height) safeBreaks.push(bodyCanvas.height);

        // ── Embed reusable PNGs for header / footer / body
        const headerPng = headerCanvas ? await pdfDoc.embedPng(this.dataUrlToUint8Array(headerCanvas.toDataURL('image/png'))) : null;
        const footerPng = footerCanvas ? await pdfDoc.embedPng(this.dataUrlToUint8Array(footerCanvas.toDataURL('image/png'))) : null;
        const bodyPng   = await pdfDoc.embedPng(this.dataUrlToUint8Array(bodyCanvas.toDataURL('image/png')));

        const headerH = headerCanvas ? headerCanvas.height * (pdfW / headerCanvas.width) : 0;
        const footerH = footerCanvas ? footerCanvas.height * (pdfW / footerCanvas.width) : 0;
        const bodyH   = bodyCanvas.height * (pdfW / bodyCanvas.width);

        // Reserve safe-area gaps so content never crashes into the header / footer bars.
        const usableH = pdfPageH - headerH - footerH - SAFE_AREA_PT * 2;

        const drawHeaderFooter = (page: any) => {
            if (headerPng) page.drawImage(headerPng, { x: 0, y: pdfPageH - headerH, width: pdfW, height: headerH });
            if (footerPng) page.drawImage(footerPng, { x: 0, y: 0,                  width: pdfW, height: footerH });
        };

        // ── Single page that fits
        if (bodyH <= usableH) {
            const page = pdfDoc.addPage([pdfW, pdfPageH]);
            drawHeaderFooter(page);
            page.drawImage(bodyPng, {
                x: 0,
                y: pdfPageH - headerH - SAFE_AREA_PT - bodyH,
                width: pdfW,
                height: bodyH,
            });
            return;
        }

        // ── Forced single page: scale body down to fit between header & footer
        if (forceSinglePage) {
            const page = pdfDoc.addPage([pdfW, pdfPageH]);
            drawHeaderFooter(page);
            const scaleDown = usableH / bodyH;
            const drawW = pdfW * scaleDown;
            const drawH = usableH;
            const offsetX = (pdfW - drawW) / 2;
            page.drawImage(bodyPng, {
                x: offsetX,
                y: pdfPageH - headerH - SAFE_AREA_PT - drawH,
                width: drawW,
                height: drawH,
            });
            return;
        }

        // ── Multi-page slice — break only at element boundaries, never mid-element.
        const ratio        = pdfW / bodyCanvas.width;
        const pageCanvasPx = usableH / ratio;        // max canvas-px height per page
        const sliceCanvasH = Math.round(pageCanvasPx);
        let cursor = 0;
        let safety = 0;

        while (cursor < bodyCanvas.height && safety++ < 100) {
            const maxEnd = cursor + pageCanvasPx;
            let nextBreak = -1;
            for (const b of safeBreaks) {
                if (b > cursor && b <= maxEnd) nextBreak = b;
                else if (b > maxEnd) break;
            }
            // Deadlock guard: a single element taller than a page → hard pixel cut.
            if (nextBreak === -1) nextBreak = Math.min(bodyCanvas.height, Math.floor(maxEnd));

            const srcY = cursor;
            const srcH = nextBreak - srcY;

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width  = bodyCanvas.width;
            sliceCanvas.height = sliceCanvasH;
            const ctx = sliceCanvas.getContext('2d')!;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(bodyCanvas, 0, srcY, bodyCanvas.width, srcH, 0, 0, bodyCanvas.width, srcH);

            const slicePng = await pdfDoc.embedPng(this.dataUrlToUint8Array(sliceCanvas.toDataURL('image/png')));
            const page = pdfDoc.addPage([pdfW, pdfPageH]);
            drawHeaderFooter(page);
            page.drawImage(slicePng, {
                x: 0,
                y: footerH + SAFE_AREA_PT,
                width: pdfW,
                height: usableH,
            });

            cursor = nextBreak;
        }
    }

    /**
     * Restyle the ATF "By Factor" signature block as a compact, visually
     * distinct info card so it can sit on the same PDF page as the patient
     * signature instead of taking a full page on its own. The data inside is
     * static / pre-filled (Adam Johnson, CPA / CFO + signature image).
     */
    private compactFactorBlock(clone: HTMLElement): void {
        const factor = clone.querySelector<HTMLElement>('.atf-sig-block--factor');
        if (!factor) return;

        factor.style.cssText = [
            'margin-top:14px',
            'padding:12px 16px',
            'background:#eef2fb',
            'border:1px solid #c7d4f0',
            'border-left:4px solid #1a2c5b',
            'border-radius:6px',
            'display:flex',
            'flex-wrap:wrap',
            'align-items:center',
            'gap:14px 22px',
            'box-sizing:border-box',
        ].join(';') + ';';

        const heading = factor.querySelector<HTMLElement>('.atf-sig-block-heading');
        if (heading) {
            heading.style.cssText = 'flex:0 0 100%;margin:0 0 4px;font-size:9pt;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#1a2c5b;';
        }

        // Each .atf-sig-table currently stacks vertically (Full Name / Title / Signature).
        // Convert them into inline columns for the compact card.
        const tables = factor.querySelectorAll<HTMLTableElement>('.atf-sig-table');
        tables.forEach(t => {
            t.style.cssText = 'flex:1 1 auto;width:auto;min-width:140px;margin:0;border-collapse:collapse;';
            const td = t.querySelector('td') as HTMLElement | null;
            if (td) td.style.cssText = 'padding:0;vertical-align:middle;';
            const label = t.querySelector<HTMLElement>('.atf-sig-label');
            if (label) label.style.cssText = 'display:block;font-size:7.5pt;font-weight:700;letter-spacing:0.4px;text-transform:uppercase;color:#4b5b80;margin-bottom:2px;';
            const line = t.querySelector<HTMLElement>('.atf-sig-line');
            if (line) line.style.cssText = 'display:block;border-bottom:1px solid #1a2c5b;padding:0 2px 2px;font-size:10pt;font-weight:600;color:#000000;line-height:1.2;';
            const img = t.querySelector<HTMLImageElement>('img');
            if (img) {
                img.style.cssText = 'display:block;max-width:140px;height:34px;object-fit:contain;object-position:left center;';
                t.style.flex = '0 0 auto';
                t.style.minWidth = '150px';
            }
        });
    }

    private prepareSigBlocksForPdf(clone: HTMLElement, docIndex: number, s: DocSignState): void {
        clone.querySelectorAll<HTMLElement>(
            '.atf-sig-label span, .ack-sig-label span, .hip-sig-label span, .rol-sig-label span'
        ).forEach(span => { span.style.display = 'none'; });

        clone.querySelectorAll<HTMLCanvasElement>('canvas').forEach(canvas => {
            const img = document.createElement('img');
            img.src   = canvas.toDataURL('image/png');
            img.style.cssText = `display:block;width:100%;height:${canvas.style.height || '90px'};object-fit:contain;object-position:left center;`;
            canvas.replaceWith(img);
        });

        clone.querySelectorAll<HTMLElement>('.inline-date-picker').forEach(dp => {
            const signer  = (dp.dataset as any)['signer'] ?? 'patient';
            const dateStr = signer === 'attorney'
                ? this.formatDate(s.attorney.selectedDate)
                : this.formatDate(s.selectedDate);
            const span = document.createElement('span');
            span.style.cssText = 'display:block;border-bottom:1px solid #374151;height:28px;line-height:28px;padding-left:4px;font-family:\'Outfit\',-apple-system,BlinkMacSystemFont,\'Segoe UI\',sans-serif;font-size:10.5pt;font-weight:500;color:#1a1a2e;';
            span.textContent = dateStr;
            dp.innerHTML = '';
            dp.appendChild(span);
        });

        ['.cl-body', '.ack-body', '.atf-body', '.hip-body', '.rol-body'].forEach(sel => {
            const bodyEl = clone.querySelector<HTMLElement>(sel);
            if (bodyEl) bodyEl.style.padding = '26px 48px';
        });

        if (docIndex === 1) {
            const attyBlock = clone.querySelector<HTMLElement>('.atf-sig-block--factor');
            if (attyBlock) attyBlock.style.borderBottom = '3px solid #1a2c5b';
        }
    }

    private dataUrlToUint8Array(dataUrl: string): Uint8Array {
        const base64 = dataUrl.split(',')[1];
        const binary = atob(base64);
        const out    = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
        return out;
    }

    private formatDate(d: string): string {
        if (!d) return '';
        return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    /**
     * Generates signed PDF and triggers a browser download, then returns to the list.
     * Does NOT upload to the server — use submitCurrentDoc() for that.
     */
    async downloadPdf(docIndex: number): Promise<void> {
        const s = this.states[docIndex];
        if (s.isDrawEmpty || !s.selectedDate) return;

        this.cdr.detectChanges();

        try {
            const { blob, date } = await this.generatePdfBlob(docIndex);
            const doc = this.docs[docIndex];

            // Store for potential bulk submit from list view
            this.signedBlobs[docIndex] = blob;
            this.signedDates[docIndex] = date;

            // Trigger browser download
            const url = URL.createObjectURL(blob);
            const a   = document.createElement('a');
            a.href = url; a.download = `${doc.id}-signed.pdf`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 10_000);

            this.snackbar.show(`"${doc.title}" signed. Go back to the list and sign remaining documents.`, 'success');
            this.esignView = 'list';
            this.cdr.detectChanges();

        } catch {
            this.snackbar.show('Failed to generate PDF. Please try again.', 'error');
        } finally {
            this.resetProgress();
        }
    }

    // ── Bulk e-sign submit ─────────────────────────────────────────────────
    submittingEsign = false;

    submitEsignDocuments(): void {
        if (!this.caseId) {
            this.snackbar.show('Case ID is missing', 'error');
            return;
        }

        // Build the documents array from locally stored blobs.
        // Use the API document type (2=Acknowledgment, 3=ATF, 4=HIPAA) as the id,
        // matching what the backend expects for /api/upload-signed.
        const docsToSubmit: { frontendIndex: number; id: number; file: File }[] = [];
        this.signedBlobs.forEach((blob, i) => {
            if (blob) {
                const apiDocType = DOC_TYPE_MAP[i as 0 | 1 | 2 | 3];
                const file = new File([blob], `${this.docs[i].id}-signed.pdf`, { type: 'application/pdf' });
                docsToSubmit.push({ frontendIndex: i, id: apiDocType, file });
            }
        });

        if (!docsToSubmit.length) {
            this.snackbar.show('No signed documents to submit', 'error');
            return;
        }

        this.submittingEsign = true;

        // Single POST to /api/upload-signed — case_id + all 3 docs in one request
        const payload = docsToSubmit.map(d => ({ id: d.id, file: d.file }));
        this.caseService.uploadSignedDocuments(this.caseId, payload, this.clientAuth ?? undefined).subscribe({
            next: () => {
                this.snackbar.show('All signed documents submitted successfully', 'success');
                this.submittingEsign = false;
                // Reset all state and close the modal
                this.initStates();
                this.mode                = 'select';
                this.esignView           = 'list';
                this.activeDocIndex      = 0;
                this.canvasesInitialized = false;
                this.close.emit();
            },
            error: () => {
                this.snackbar.show('Error submitting signed documents', 'error');
                this.submittingEsign = false;
                this.cdr.detectChanges();
            },
        });
    }

    // ── Single-doc e-sign submit ───────────────────────────────────────────
    submittingCurrentDoc = false;

    /**
     * Generates the signed PDF and uploads it to the server immediately.
     */
    async submitCurrentDoc(docIndex: number): Promise<void> {
        const s = this.states[docIndex];
        if (s.isDrawEmpty || !s.selectedDate || !this.caseId) return;

        this.submittingCurrentDoc = true;
        this.cdr.detectChanges();

        try {
            const { blob, date } = await this.generatePdfBlob(docIndex);
            const doc = this.docs[docIndex];
            const apiDocType = DOC_TYPE_MAP[docIndex as 0 | 1 | 2 | 3];
            const file = new File([blob], `${doc.id}-signed.pdf`, { type: 'application/pdf' });

            // Store locally
            this.signedBlobs[docIndex] = blob;
            this.signedDates[docIndex] = date;

            // Upload immediately
            this.caseService.uploadSignedDocuments(this.caseId, [{ id: apiDocType, file }], this.clientAuth ?? undefined).subscribe({
                next: () => {
                    this.snackbar.show(`"${doc.title}" signed and submitted successfully`, 'success');
                    this.submittingCurrentDoc = false;
                    this.resetProgress();

                    if (this.inline) {
                        // Mark as submitted so its tab disappears, then move to next remaining doc
                        this.clientSubmittedIndices.add(docIndex);
                        const remaining = this.esignDocs;
                        if (remaining.length > 0) {
                            this.activeDocIndex = remaining[0].index;
                            this.esignView = 'sign';
                            this.canvasesInitialized = false;
                            this.loadDocCaseData(this.activeDocIndex);
                        } else {
                            // All done — let host page show the success state
                            this.close.emit();
                        }
                    } else {
                        this.esignView = 'list';
                        this.close.emit();
                    }
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.snackbar.show(`Failed to submit "${doc.title}". Please try again.`, 'error');
                    this.submittingCurrentDoc = false;
                    this.cdr.detectChanges();
                }
            });

        } catch {
            this.snackbar.show('Failed to generate PDF. Please try again.', 'error');
            this.submittingCurrentDoc = false;
            this.resetProgress();
        }
    }

    handleClose(): void {
        // When the user is on the sign canvas, × goes back to the e-sign list
        // instead of closing the whole modal.
        if (this.mode === 'esign' && this.esignView === 'sign') {
            this.goBackToEsignList();
            return;
        }
        this.initStates();
        this.mode                = 'select';
        this.esignView           = 'list';
        this.activeDocIndex      = 0;
        this.canvasesInitialized = false;
        this.close.emit();
    }

}
